import classNames from 'classnames';
import { PageNumber } from 'components';
import { selectHistoryDataLoading } from 'features/integrations/state/selectors/integrationSelectors';
import React, { useEffect, useRef, useState } from 'react';
import { FaCogs } from 'react-icons/fa';
import { FaChevronLeft, FaPause, FaPlay } from 'react-icons/fa6';
import {
  MdOutlineSignalCellularAlt,
  MdOutlineSignalCellularAlt1Bar,
  MdOutlineSignalCellularAlt2Bar,
} from 'react-icons/md';
import { useSelector } from 'react-redux';
import Popup from 'reactjs-popup';
import { fetchAwareImageObjectUrl } from 'utils/awareImageUtils';
import styles from './AwareFloodDataSheet.module.css';

export default function AwareFloodDataSheet({ device, onClose, onSettingsOpen }) {
  const historyDataLoading = useSelector(selectHistoryDataLoading);
  const [page, setPage] = useState(0);
  const [uniqueImages, setUniqueImages] = useState([]);
  const [currentImg, setCurrImg] = useState(0);
  const [playback, setPlayback] = useState();
  const currentImgRef = useRef(0);

  useEffect(() => {
    return () => {
      clearInterval(playback);
    };
  }, [playback]);

  useEffect(() => {
    let isMounted = true;

    const loadImages = async () => {
      const imageMap = new Map();
      for (let i = 0; i < device.data.length; i++) {
        const dataPoint = device.data[i];
        const key = dataPoint?.img_link_url;
        if (!imageMap.has(key)) {
          const imageURL = await fetchAwareImageObjectUrl(key);
          if (!imageURL) continue;

          imageMap.set(key, {
            url: imageURL,
            index: i,
            ts: dataPoint.ts,
          });
        }
      }

      if (isMounted) {
        setUniqueImages([...imageMap.values()]);
        setCurrImg(0);
      }
    };
    loadImages();

    return () => {
      isMounted = false;
    };
  }, [device]);

  useEffect(() => {
    return () => {
      uniqueImages.forEach((image) => {
        URL.revokeObjectURL(image.url);
      });
    };
  }, [uniqueImages]);

  useEffect(() => {
    setCurrImg(page);
  }, [page, uniqueImages]);

  const toggleImagePlayback = () => {
    if (playback) {
      clearInterval(playback);
      setPlayback(null);
    } else {
      const playbackFunc = () => {
        const newImg = (currentImgRef.current + 1) % uniqueImages.length;
        setCurrImg(newImg);
        currentImgRef.current = newImg;
      };

      setPlayback(setInterval(playbackFunc, 1000));
    }
  };

  const SignalStrength = ({ rssi }) => {
    rssi = -parseInt(rssi);
    if (rssi <= -50)
      return (
        <div>
          <MdOutlineSignalCellularAlt /> (High)
        </div>
      );
    else if (rssi <= -71)
      return (
        <div>
          <MdOutlineSignalCellularAlt2Bar /> (Medium)
        </div>
      );
    else if (rssi <= -100)
      return (
        <div>
          <MdOutlineSignalCellularAlt1Bar /> (Low)
        </div>
      );
    else return 'No Signal';
  };
  if (!device || !device.data || device.data.length === 0) {
    // If historical data is still loading, show loading indicator
    if (historyDataLoading) {
      return (
        <div className="flex-fill h-100 d-flex flex-column justify-content-center align-items-center">
          <div className="spinner-border mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted">Loading device data...</p>
          <button className="btn btn-secondary mt-2" onClick={onClose}>
            Back
          </button>
        </div>
      );
    }
    return <p onClick={onClose}>No data available for this device.</p>;
  }

  return (
    <div className="flex-fill h-100 min-h-0 d-flex flex-column">
      {/* Header */}
      <div className="d-flex justify-content-between border-bottom-none border-bottom flex-shrink-0">
        <button className="btn" onClick={onClose}>
          <FaChevronLeft />
          <div>Back</div>
        </button>

        <div className="text-center">
          <p className="h2">Device Data</p>
          <div>Time Stamp: {new Date(device.data[page].ts).toLocaleString()}</div>
        </div>

        <button className="btn" onClick={onSettingsOpen}>
          <FaCogs size={25} />
        </button>
      </div>

      {/* Data */}
      <div className="flex-fill min-h-0 overflow-auto" style={{ minHeight: 540 }}>
          <div className="d-flex justify-content-between border-bottom border-secondary">
            <p>Device Alias:</p> <div>{device.alias}</div>
          </div>

          <div className="d-flex justify-content-between border-bottom border-secondary">
            <p>Signal Strength (Bars): </p>
            <div>{<SignalStrength rssi={device.data[page].rssi} />}</div>
          </div>

          <div className="d-flex justify-content-between border-bottom border-secondary">
            <p>Battery:</p>
            <div>{device.data[page].battery} V</div>
          </div>

          <div className="d-flex justify-content-between border-bottom border-secondary">
            <p>Rise Rate Indicator:</p>
            <p className={device.data[page].ffi1 === 1 ? 'text-danger' : ''}>
              {device.data[page].ffi1 === 1 ? 'Flooding Alert!' : 'No Flooding Alert'}
            </p>
          </div>

          <div className="d-flex justify-content-between border-bottom border-secondary">
            <p>Drop Rate Indicator:</p>
            <div className={device.data[page].drInd === 1 ? 'text-danger' : ''}>
              {device.data[page].drInd === 1 ? 'Drop Rate Alert!' : 'No Drop Rate Alert'}
            </div>
          </div>

          <div className="d-flex justify-content-between border-bottom border-secondary">
            <p>Depth Trigger Indicator:</p>
            <div
              className={
                (device?.data[page]?.depth1 ??
                0 > (device?.water_level_adj ?? 0) + device?.device_settings?.depthDet1 ??
                0)
                  ? 'text-danger'
                  : ''
              }
            >
              {(device?.data[page]?.depth1 ??
              0 > (device?.water_level_adj ?? 0) + device?.device_settings?.depthDet1 ??
              0)
                ? 'Depth Trigger Alert!'
                : 'No Depth Trigger Alert'}
            </div>
          </div>

          <div className="d-flex justify-content-between border-bottom border-secondary">
            <p>Cell Signal Strength:</p>
            <div>{device.data[page].rssi} dB</div>
          </div>

          <div className="d-flex justify-content-between border-bottom border-secondary">
            <p>Node ID:</p> <div>{device.node_id}</div>
          </div>

          <div className="d-flex justify-content-between border-bottom border-secondary">
            <p>GPS Location:</p>
            <div>{`${device.location.lat} N ${device.location.lng} E`}</div>
          </div>

          <div className="d-flex justify-content-between border-bottom border-secondary">
            <p>Elevation:</p>
            <div>{`${device.elev ?? 0} ft/ ${((device.elev ?? 0) * 0.3048).toFixed(3)} m`}</div>
          </div>

          <div className="d-flex justify-content-between border-bottom border-secondary">
            <p>Barometric Pressure:</p>
            <div>{`${device.data[page].baro} mBar/ ${(device.data[page].baro * 0.02952998).toFixed(
              3,
            )} inHg`}</div>
          </div>

          <div className="d-flex justify-content-between border-bottom border-secondary">
            <p>Water Level Adjustment:</p>
            <div>{`${device?.water_level_adj || 0} in/ ${(
              device?.water_level_adj || 0 / 12
            ).toFixed(3)} ft`}</div>
          </div>

          <Popup
            on="hover"
            position="bottom left"
            trigger={
              <div className="d-flex justify-content-between border-bottom border-secondary">
                <p>Sensor Depth:</p>
                <div>{`${device.data[page].depth1} in/ ${(device.data[page].depth1 / 12).toFixed(
                  3,
                )} ft`}</div>
              </div>
            }
          >
            <div className="bg-white text-black rounded p-2">
              <p className="m-0">Flood Sensor Pressure/Transducer Reading</p>
            </div>
          </Popup>

          <Popup
            on="hover"
            position="bottom left"
            trigger={
              <div className="d-flex justify-content-between border-bottom border-secondary">
                <p>Actual Depth:</p>
                <div>{`${device.data[page].sensordepths} in/ ${(
                  device.data[page].sensordepths / 12
                ).toFixed(3)} ft`}</div>
              </div>
            }
          >
            <div className="bg-white text-black rounded p-2">
              <p>Adjusted Depths</p>
              <p className="m-0">
                ("Water Level Adjustment + Flood Sensor Pressure/Transducer Reading").
              </p>
            </div>
          </Popup>

          <div className="d-flex justify-content-between border-bottom border-secondary">
            <p>Water Temperature:</p>
            <div>{`${device.data[page].h2oTemp} °C/ ${
              (device.data[page].h2oTemp * 9) / 5
            } °F`}</div>
          </div>

          <div className="d-flex justify-content-between border-bottom border-secondary">
            <p>External Device Data:</p>
            <div>{`${device.data[page].soilSDI}, ${device.data[page].soilTempSDI}`}</div>
          </div>

          <div className="d-flex justify-content-between border-bottom border-secondary">
            <p>Gateway Type:</p>
            <div>{device.gatewayType ? 'FirstNet Cellular' : 'Satellite'}</div>
          </div>

          {/* Images */}
          {uniqueImages && uniqueImages[currentImg] && (
            <div>
              <div className="position-relative d-inline-block">
                <img
                  className="img-fluid"
                  src={uniqueImages[currentImg].url}
                  alt={'Aware Flood Sensor'}
                />

                <button
                  className="position-absolute top-0 bottom-0 start-0 end-0 d-flex justify-content-center align-items-center"
                  onClick={toggleImagePlayback}
                >
                  {playback && <FaPause />}
                  {!playback && <FaPlay />}
                </button>
              </div>

              <p>{`${new Date(uniqueImages[currentImg].ts).toLocaleString()} (${
                currentImg + 1
              }/${uniqueImages.length})`}</p>
            </div>
          )}
      </div>

      {/* Page Numbers */}
      <div className="flex-shrink-0">
        <PageNumber pages={device.data.length} value={page} onChange={setPage} />
      </div>
    </div>
  );
}
